/**
 * The face a relationship presents.
 *
 * An identity is a node on the `profile` schema — the ones Self → Profile manages
 * — and a realm carries `profile_node_id`: "the face the user presents in this
 * context". A realm PROPOSES; the relationship ANSWERS.
 *
 * The reason the relationship has to answer at all: an EP's grant is the union of
 * its attached realms. Permissions union cleanly, which is the whole point of
 * realms — two names do not. So where realms disagree, nothing is asserted until
 * somebody settles it, and picking one by an implicit rule would mean you could
 * not tell what an app sees without simulating the resolver, on the surface whose
 * entire job is telling you exactly that.
 *
 * Mirrors app/authz/identity.py. Where the two could drift, this one is only ever
 * a display — the server decides what an app actually reads.
 */

/** The distinct faces this EP's realms propose. */
export function realmFaces(ep) {
  const realms = (ep?.realms || []);
  const ids = realms.map(r => r.profile_node_id).filter(Boolean);
  return [...new Set(ids.map(String))];
}

/** The single face presented, or null when nothing settles it. */
export function resolveFace(ep) {
  if (!ep) return null;
  if (ep.profile_node_id) return String(ep.profile_node_id);
  const faces = realmFaces(ep);
  return faces.length === 1 ? faces[0] : null;
}

/** Do the realms disagree with nothing settling it? The one case to ask about. */
export function faceIsContested(ep) {
  return !ep?.profile_node_id && realmFaces(ep).length > 1;
}

/**
 * The resolved face, as the SERVER resolved it.
 *
 * The client cannot do this itself, and should not try: resolution ends at the
 * owner's default, which is nowhere in the grant, and "may the app read identities
 * at all" is a question about the effective grant rather than the consent blob. The
 * server sends `ep.identity`; this unwraps it, and falls back to what can be worked
 * out locally for a payload that predates the field.
 */
export function faceOf(ep) {
  const i = ep?.identity;
  if (i) {
    return {
      id: i.node_id ? String(i.node_id) : null,
      source: i.source || 'none',
      contested: !!i.contested,
      visible: !!i.visible,
      showsAll: !!i.shows_all,
    };
  }
  const faces = realmFaces(ep);
  const chosen = ep?.profile_node_id ? String(ep.profile_node_id)
    : (faces.length === 1 ? faces[0] : null);
  return {
    id: chosen,
    source: ep?.profile_node_id ? 'chosen' : (faces.length === 1 ? 'realm' : 'none'),
    contested: !ep?.profile_node_id && faces.length > 1,
    visible: true,          // unknown here; never claim a grant is missing
  };
}

/** Why this relationship shows what it shows — the sentence beside the name. */
export function faceReason(ep) {
  if (!ep) return null;
  const { source } = faceOf(ep);
  if (source === 'all') return 'every identity, because you said so';
  if (source === 'chosen') return 'you chose this';
  if (source === 'realm') {
    const faces = realmFaces(ep);
    const from = (ep.realms || []).find(r => String(r.profile_node_id) === faces[0]);
    return from ? `from ${from.name}` : 'from its realm';
  }
  if (source === 'default') return 'your default identity';
  if (realmFaces(ep).length > 1) return `${realmFaces(ep).length} realms disagree`;
  return 'no realm names a face';
}

/**
 * The face a grant WOULD present, resolved on the consent screen.
 *
 * The same ladder the server walks after approval (app/authz/identity.py), applied to
 * a grant that does not exist yet: your pick, else the realms you have selected when
 * they agree, else your default. Stated here so the screen can say who you will be
 * before you agree to it, rather than only afterwards on the relationships page.
 *
 * `realms` are the preview's realm rows (each with `profile_node_id`), `realmIds` the
 * ones selected, `identities` the preview's list with `is_default` marked.
 */
export function consentFace({ realms = [], realmIds = [], identities = [], chosenId = null,
                              showAll = false }) {
  // "Show them all" is an answer to the same question, so it outranks the ladder
  // rather than sitting beside it.
  if (showAll) return { id: null, source: 'all', contested: false, showsAll: true };
  if (chosenId) return { id: String(chosenId), source: 'chosen', contested: false };

  const selected = realms.filter(r => realmIds.map(String).includes(String(r.id)));
  const proposed = [...new Set(selected.map(r => r.profile_node_id).filter(Boolean).map(String))];
  if (proposed.length === 1) {
    const from = selected.find(r => String(r.profile_node_id) === proposed[0]);
    return { id: proposed[0], source: 'realm', realmName: from?.name || null, contested: false };
  }

  const fallback = identities.find(i => i.is_default) || null;
  return {
    id: fallback ? String(fallback.id) : null,
    source: fallback ? 'default' : 'none',
    contested: proposed.length > 1,
  };
}

/**
 * WHICH IDENTITIES THIS GRANT MAY PRESENT.
 *
 * Not all of them. A realm carries `profile_node_id` — the face it proposes — and
 * offering every identity you own ignored what the realm asked for, on a list
 * that grows with every identity you make.
 *
 * So the offer is: the faces the CHOSEN realms propose, plus your default. Two
 * entries at most in the ordinary case ("Preeti, from Practice" or "Priya, your
 * default"), which is the choice a person actually has — and one entry when the
 * realm proposes your default anyway, where the section states rather than asks.
 *
 * The default is always in, and that is the deliberate part: a realm naming a
 * face is a suggestion, not a lock, and a screen that can only offer what a realm
 * proposed would let a realm decide who you are.
 */
export function faceOffer({ realms = [], realmIds = [], identities = [] }) {
  const picked = realms.filter(r => realmIds.map(String).includes(String(r.id)));
  const proposed = new Set(picked.map(r => r.profile_node_id).filter(Boolean).map(String));
  return identities.filter(i => i.is_default || proposed.has(String(i.id)));
}

/** name → the identity node, for rendering. */
export function faceNode(id, profileNodes) {
  if (!id) return null;
  return (profileNodes || []).find(n => String(n.id) === String(id)) || null;
}

export function faceLabel(id, profileNodes) {
  const n = faceNode(id, profileNodes);
  if (!n) return null;
  return n.value_json?.display_name || n.title || null;
}
